<template>
  <div class="v2-home v2-container v2-section">
    <header class="v2-home__welcome">
      <div class="v2-home__welcome-copy">
        <p class="v2-overline">{{ greetingTime }},</p>
        <h1 class="v2-headline v2-headline--display-lg v2-home__title">
          {{ firstName }}
        </h1>
      </div>

      <div v-if="hasCustomActions" class="v2-home__custom-actions">
        <button
          v-if="isAmpiMember"
          type="button"
          class="v2-home__custom-button v2-home__custom-button--ampi"
          @click="redirectToValoresAMPI"
        >
          <v-icon size="18">mdi-open-in-new</v-icon>
          <span>Ir a Valores AMPI</span>
        </button>

        <BaseButton
          v-if="isImefMember"
          variant="amber"
          size="md"
          icon="mdi-chart-line"
          @click="goToImefPoints"
        >
          Puntos EPC
        </BaseButton>
      </div>
    </header>

    <section v-if="isActiveMember" class="v2-home__membership">
      <div class="v2-membership-card">
        <div class="v2-membership-card__left">
          <span class="v2-membership-card__type-label">
            Tipo de membresía:
          </span>
          <h2 class="v2-membership-card__type">
            {{ membershipTypeLabel }}
          </h2>
          <p class="v2-membership-card__expiry">
            <v-icon size="16" class="v2-membership-card__expiry-icon">mdi-calendar</v-icon>
            Vence el {{ expirationDateLabel || "N/D" }}
          </p>
        </div>
        <div class="v2-membership-card__right">
          <div class="v2-membership-card__stat">
            <template v-if="isInGracePeriod">
              <span class="v2-membership-card__grace-note">
                <v-icon size="14" class="v2-membership-card__grace-icon">
                  mdi-alert-circle-outline
                </v-icon>
                Estás en días de tolerancia
              </span>
            </template>
            <template v-else>
              <span class="v2-membership-card__stat-label">
                Días restantes
              </span>
              <span class="v2-membership-card__stat-value">
                {{ daysRemaining }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </section>

    <MembershipStatusBanner
      v-if="isNonActiveMember"
      :status="status"
      :expiration-date-label="expirationDateLabel"
    />

    <section class="v2-home__main">
      <div class="v2-home__credential">
        <MemberCredential
          :user="user"
          :credential="credentialInfo"
          :status="status"
          :locked="isNonActiveMember"
        />
      </div>

      <div class="v2-home__events" :class="{ 'v2-home__events--locked': isExpired }">
        <header v-if="!isExpired" class="v2-home__events-head">
          <SectionHeader title="Próximos eventos" description="Eventos disponibles en tu capítulo. Inscripciones abiertas.">
            <template #actions>
              <BaseButton
                variant="ghost"
                size="sm"
                to="/member-v2/events"
                icon="mdi-arrow-right"
              >
                Ver todos
              </BaseButton>
            </template>
          </SectionHeader>
        </header>

        <header v-else class="v2-home__events-head v2-home__events-head--locked">
          <div class="v2-home__events-locked-label">
            <span class="v2-badge v2-badge--neutral">BLOQUEADO</span>
            <p class="v2-body v2-body--md v2-body--muted">
              Los eventos se desbloquean al renovar tu membresía
            </p>
          </div>
        </header>

        <UpcomingEvents
          :items="upcomingItems"
          :loading="loadingHome"
          :locked="isExpired"
          :renew-link="renewLink"
        />
      </div>
    </section>

    <section v-if="user" class="v2-home__actions-wrap">
      <div class="v2-home__actions">
        <article class="v2-action-card v2-action-card--amber">
          <div class="v2-action-card__icon v2-action-card__icon--amber">
            <v-icon size="28">mdi-update</v-icon>
          </div>
          <h3 class="v2-action-card__title">Renovar</h3>
          <p
            v-if="!hideRenewButton && !canRenew && nextRenewDateLabel"
            class="v2-action-card__desc"
          >
            Podrás renovar a partir del {{ nextRenewDateLabel }}.
          </p>
          <p v-else class="v2-action-card__desc">
            No permitas que se inactive tu membresía, renueva ahora.
          </p>
          <BaseButton
            v-show="!hideRenewButton"
            variant="amber"
            size="md"
            block
            icon="mdi-refresh"
            :disabled="!canRenew"
            @click="onRenewClick"
          >
            {{ renewButtonLabel }}
          </BaseButton>
        </article>

        <article class="v2-action-card v2-action-card--navy">
          <div class="v2-action-card__icon v2-action-card__icon--navy">
            <v-icon size="28">mdi-file-document</v-icon>
          </div>
          <h3 class="v2-action-card__title">Constancia</h3>
          <p class="v2-action-card__desc">
            Descarga tu constancia digital en PDF.
          </p>
          <v-tooltip top :disabled="hasCertificate" max-width="320">
            <template #activator="{ on }">
              <span v-on="on">
                <BaseButton
                  variant="navy-outline"
                  size="md"
                  block
                  :loading="downloadingCertificate"
                  :disabled="!hasCertificate"
                  @click="onCertificateClick"
                >
                  Descargar constancia
                </BaseButton>
              </span>
            </template>
            <span v-if="!user || !user.hasDiplomaTemplate || !user.diplomaUrl">
              Aún no hay una plantilla de constancia configurada para tu membresía.
              Contacta a tu cámara para solicitarla.
            </span>
            <span v-else>
              Tu membresía debe estar activa para descargar la constancia.
            </span>
          </v-tooltip>
        </article>
      </div>
    </section>

    <button
      v-if="showFab"
      type="button"
      class="v2-fab"
      @click="onFabClick"
    >
      <v-icon size="20">mdi-refresh</v-icon>
      <span>{{ renewButtonLabel }}</span>
      <v-icon size="16">mdi-chevron-right</v-icon>
    </button>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations, mapState } from "vuex";
import moment from "moment";
import "moment/locale/es";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import SectionHeader from "@/components/memberDashboardV2/ui/SectionHeader.vue";
import MemberCredential from "./MemberCredential.vue";
import MembershipStatusBanner from "./MembershipStatusBanner.vue";
import UpcomingEvents from "./UpcomingEvents.vue";
import { isAmpiOrganization } from "@/share/validateOrganization";
import { isImefOrganization } from "@/share/imef";
import {
  resolveFirstName,
  resolveExpirationDate,
  resolveMembershipType,
  isExpired,
  isProspect,
  formatDate,
  resolveRenewLink,
  resolveRenewCtaLabel,
  resolveAbleToRenewAffiliation,
  resolveCanAccessToCheckout,
  shouldHideRenewButton,
  resolveNextRenewDate,
  isInIndulgencePeriod,
} from "./homeHelpers";

moment.locale("es");

const UPCOMING_EVENTS_LIMIT = 4;

export default {
  name: "MemberDashboardV2Home",
  components: {
    BaseButton,
    SectionHeader,
    MemberCredential,
    MembershipStatusBanner,
    UpcomingEvents,
  },
  data() {
    return {
      loadingHome: false,
      upcomingEvents: [],
      downloadingCertificate: false,
      seniority: "3 años",
      canMakePayment: false,
      ableToRenewAffiliation: { national: false, local: false },
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("membershipProfile", ["userMembership"]),
    ...mapState("member", ["credential"]),
    ...mapState("membershipUsers", ["credential", "ownerAffiliation"]),
    memberCredential() {
      return this.$store.state.member.credential || {};
    },
    membershipUserCredential() {
      return this.$store.state.membershipUsers.credential || {};
    },
    firstName() {
      return resolveFirstName(this.user) || "miembro";
    },
    status() {
      return this.user ? this.user.validationStatus : null;
    },
    isActiveMember() {
      return Boolean(this.user) && this.status === "ACTIVE";
    },
    isNonActiveMember() {
      return Boolean(this.user) && this.status !== "ACTIVE";
    },
    isExpired() {
      return isExpired(this.user);
    },
    isProspect() {
      return isProspect(this.user);
    },
    greetingTime() {
      const h = new Date().getHours();
      if (h < 12) return "Buenos días";
      if (h < 19) return "Buenas tardes";
      return "Buenas noches";
    },
    headOfficeId() {
      const headOffice = this.user && this.user.headOffice;
      if (!headOffice) return null;
      return typeof headOffice === "object"
        ? headOffice.id || headOffice._id
        : headOffice;
    },
    isAmpiMember() {
      return isAmpiOrganization(this.headOfficeId);
    },
    isImefMember() {
      return isImefOrganization(this.headOfficeId);
    },
    hasCustomActions() {
      return this.isAmpiMember || this.isImefMember;
    },
    expirationDateLabel() {
      const date = resolveExpirationDate(this.user);
      if (!date) return null;
      return formatDate(date, "DD [de] MMMM [de] YYYY");
    },
    showFab() {
      return this.isExpired || this.status === "PROSPECTO" || this.status === "APPROVED";
    },
    upcomingItems() {
      return (this.upcomingEvents || [])
        .slice(0, UPCOMING_EVENTS_LIMIT)
        .map((ev) => ({ event: ev }));
    },
    credentialInfo() {
      const fromMember = this.memberCredential && this.memberCredential.credentialInformation;
      if (fromMember) return fromMember;
      const fromMembership = this.membershipUserCredential && this.membershipUserCredential.credentialInformation;
      if (fromMembership) return fromMembership;
      return null;
    },
    membershipTypeLabel() {
      const t = resolveMembershipType(this.user);
      return t && t !== "N/D" ? t : "Membresía";
    },
    daysRemaining() {
      const date = resolveExpirationDate(this.user);
      if (!date) return "N/D";
      const m = moment(date);
      if (!m.isValid()) return "N/D";
      const diff = m.diff(moment(), "days");
      return diff >= 0 ? String(diff) : "0";
    },
    isInGracePeriod() {
      if (!this.user || this.status !== "ACTIVE") return false;

      return (
        Number(this.daysRemaining) === 0 ||
        isInIndulgencePeriod(this.user, this.ownerAffiliation)
      );
    },
    seniorityLabel() {
      if (!this.user) return this.seniority;
      const created = this.user.affiliation
        && this.user.affiliation.local
        && this.user.affiliation.local.details
        && this.user.affiliation.local.details.affiliateInformation
        && this.user.affiliation.local.details.affiliateInformation.createdAt;
      if (!created) return this.seniority;
      const start = moment(created);
      if (!start.isValid()) return this.seniority;
      const years = moment().diff(start, "years");
      if (years > 0) return `${years} ${years === 1 ? "año" : "años"}`;
      const months = moment().diff(start, "months");
      if (months > 0) return `${months} ${months === 1 ? "mes" : "meses"}`;
      return "Menos de 1 mes";
    },
    ableToRenewNational() {
      return !!(this.ableToRenewAffiliation && this.ableToRenewAffiliation.national);
    },
    canRenew() {
      if (this.status !== "ACTIVE") return true;

      return resolveCanAccessToCheckout(
        this.user,
        this.ableToRenewAffiliation,
        this.canMakePayment
      );
    },
    hideRenewButton() {
      if (this.status !== "ACTIVE") return false;

      return shouldHideRenewButton(this.user);
    },
    nextRenewDateLabel() {
      const hierarchyType =
        (this.user && this.user.headOffice && this.user.headOffice.hierarchyType) ||
        "ONE_LEVEL";
      const date = resolveNextRenewDate(
        this.user && this.user.affiliation,
        hierarchyType
      );
      if (!date) return null;
      return formatDate(date, "DD [de] MMMM [de] YYYY");
    },
    renewButtonLabel() {
      return resolveRenewCtaLabel(this.status);
    },
    renewLink() {
      return resolveRenewLink(this.user, this.userMembership || {});
    },
    hasCertificate() {
      return Boolean(
        this.user &&
          this.user.validationStatus === "ACTIVE" &&
          this.user.hasDiplomaTemplate &&
          this.user.diplomaUrl
      );
    },
  },
  methods: {
    ...mapActions("member", ["fetchMemberCredentials"]),
    ...mapActions("membershipUsers", ["fetchMembershipCredentials", "fetchOwnerAffiliation"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    ...mapMutations("notification", ["show"]),
    async verifyPaymentAbility() {
      try {
        const response = await Vue.axios({
          method: "GET",
          url: "/users/verify-payment-ability",
        });
        this.canMakePayment = Boolean(response.data && response.data.canMakePayment);
      } catch (error) {
        console.error("Error verifying payment ability:", error);
        this.canMakePayment = false;
      }
    },
    updateNationalLocalPayments(affiliation = null) {
      if (!affiliation) return;
      this.ableToRenewAffiliation = resolveAbleToRenewAffiliation(affiliation);
    },
    async loadHomeIndicators() {
      this.loadingHome = true;
      try {
        const { data } = await Vue.axios({
          method: "GET",
          url: "/home-indicators",
        });
        this.upcomingEvents = Array.isArray(data.upcomingEvents) ? data.upcomingEvents : [];
      } catch (e) {
        this.upcomingEvents = [];
      } finally {
        this.loadingHome = false;
      }
    },
    async loadCredentials() {
      if (!this.user) return;
      try {
        if (this.user.role === "MEMBER") {
          await this.fetchMemberCredentials();
        } else if (this.user.role === "MEMBERSHIP_USER") {
          await Promise.all([
            this.fetchMembershipCredentials(),
            this.fetchOwnerAffiliation(),
          ]);
        }
      } catch (e) {
        // el shell V2 sigue funcionando aunque la credencial falle
      }
    },
    onFabClick() {
      this.$router.push(this.renewLink);
    },
    onRenewClick() {
      this.$router.push(this.renewLink);
    },
    onPaymentPlansClick() {
      this.$router.push("/member/payment-plans");
    },
    redirectToValoresAMPI() {
      window.open("https://valoresampi.mx/", "_blank");
    },
    goToImefPoints() {
      this.$router.push({
        path: "/member-v2/history",
        query: { tab: "eventPoints" },
      });
    },
    onCertificateClick() {
      const userId = this.user && this.user._id;
      if (!userId) return;
      this.downloadingCertificate = true;
      Vue.axios({
        method: "POST",
        url: "/users/certificate",
        data: { userId },
      })
        .then((response) => {
          const constancy = response.data && response.data.constancy;
          if (constancy) {
            window.open(constancy, "_blank");
          } else {
            this.show({
              text: "No se pudo generar la constancia. Verifica que tengas una membresía activa con configuración de diploma.",
              color: "warning",
            });
          }
        })
        .catch((error) => {
          console.error("Error generando constancia:", error);
          this.show({
            text: "Error al generar la constancia",
            color: "error",
          });
        })
        .finally(() => {
          this.downloadingCertificate = false;
        });
    },
  },
  async mounted() {
    this.loading();
    try {
      this.ableToRenewAffiliation = resolveAbleToRenewAffiliation(
        this.user && this.user.affiliation
      );
      await Promise.all([
        this.loadHomeIndicators(),
        this.loadCredentials(),
        this.verifyPaymentAbility(),
      ]);
    } finally {
      this.loaded();
    }
  },
  watch: {
    "user.affiliation": {
      handler(newAffiliation) {
        this.updateNationalLocalPayments(newAffiliation);
      },
    },
  },
};
</script>

<style scoped>
.v2-home {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  position: relative;
}

.v2-home__welcome {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  min-height: 96px;
}

.v2-home__welcome-copy {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.v2-home__title {
  margin: 0;
}

.v2-home__custom-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-left: auto;
}

.v2-home__custom-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
  padding: var(--space-3) var(--space-5);
  border: 1px solid transparent;
  border-radius: var(--radius-full);
  color: #ffffff;
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-bold);
  line-height: var(--lh-button);
  white-space: nowrap;
  cursor: pointer;
  transition: background-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-press);
}

.v2-home__custom-button--ampi {
  background-color: #70b578;
}

.v2-home__custom-button--ampi:hover {
  background-color: #609e68;
  box-shadow: var(--shadow-card);
}

.v2-home__custom-button:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

@media (max-width: 767.98px) {
  .v2-home__welcome {
    align-items: flex-start;
    flex-direction: column;
  }

  .v2-home__custom-actions {
    justify-content: flex-start;
    margin-left: 0;
  }
}

.v2-home__main {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--gutter-desktop);
  align-items: stretch;
}

.v2-home__credential {
  min-height: 480px;
  display: flex;
}

.v2-home__credential > * {
  flex: 1 1 auto;
  width: 100%;
}

@media (min-width: 1024px) {
  .v2-home__main {
    grid-template-columns: minmax(320px, 380px) 1fr;
  }
}

.v2-home__events {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.v2-home__events-head {
  margin-bottom: var(--space-4);
}

.v2-home__events-head--locked {
  display: flex;
  align-items: center;
}

.v2-home__events-locked-label {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
}

.v2-home__events--locked :deep(.v2-upcoming-events__list),
.v2-home__events--locked :deep(.v2-event-item) {
  filter: blur(2px);
  pointer-events: none;
  opacity: 0.5;
}

.v2-home__membership {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.v2-home__actions-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.v2-membership-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  background: var(--gradient-chamber);
  color: var(--color-navy-on);
  border-radius: 1.25rem;
  padding: var(--space-8);
  box-shadow: var(--shadow-card);
}

@media (min-width: 1024px) {
  .v2-membership-card {
    flex-direction: row;
    align-items: stretch;
    min-height: 200px;
  }
}

.v2-membership-card__left {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-3);
  position: relative;
  z-index: 1;
}

.v2-membership-card__type-label {
  font-size: var(--fs-label-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  font-weight: var(--fw-semibold);
  color: var(--color-navy-on-soft);
}

.v2-membership-card__type {
  margin: 0;
  font-family: var(--font-headline);
  font-size: var(--fs-display-lg);
  font-weight: var(--fw-bold);
  color: var(--color-navy-on);
  line-height: var(--lh-tight);
}

.v2-membership-card__expiry {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-bold);
  color: var(--color-amber);
}

.v2-membership-card__expiry-icon {
  color: var(--color-amber) !important;
}

.v2-membership-card__right {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  gap: var(--space-4);
  position: relative;
  z-index: 1;
}

@media (min-width: 1024px) {
  .v2-membership-card__right {
    flex-direction: row;
    align-items: center;
    min-width: auto;
  }
}

.v2-membership-card__stat {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-1);
  background-color: rgba(255, 255, 255, 0.10);
  border-radius: var(--radius-xl);
  padding: var(--space-3) var(--space-5);
  width: 140px;
  height: 140px;
  aspect-ratio: 1 / 1;
}

.v2-membership-card__stat-label {
  font-size: var(--fs-label-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  font-weight: var(--fw-semibold);
  color: var(--color-navy-on-soft);
}

.v2-membership-card__stat-value {
  font-family: var(--font-headline);
  font-size: var(--fs-display-md);
  font-weight: var(--fw-bold);
  color: var(--color-amber);
  line-height: var(--lh-tight);
}

.v2-membership-card__grace-note {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-amber);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-snug);
}

.v2-membership-card__grace-icon {
  color: var(--color-amber) !important;
}

.v2-home__actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .v2-home__actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.v2-action-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  text-align: center;
  padding: var(--space-5);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  transition: box-shadow var(--transition-base), transform var(--transition-base);
}

.v2-action-card:hover {
  box-shadow: var(--shadow-card-hover);
}

.v2-action-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  margin: 0 auto;
  background-color: var(--color-neutral-100);
}

.v2-action-card__icon--amber {
  background-color: var(--color-amber-soft-2);
  color: var(--color-amber);
}

.v2-action-card__icon--blue {
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
}

.v2-action-card__icon--navy {
  background-color: rgba(27, 39, 62, 0.08);
  color: var(--color-navy-deep);
}

.v2-action-card__title {
  margin: 0;
  font-family: var(--font-headline);
  font-size: var(--fs-display-sm);
  font-weight: var(--fw-bold);
  color: var(--color-text);
}

.v2-action-card__desc {
  margin: 0 0 var(--space-2);
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  line-height: var(--lh-snug);
  flex: 1 1 auto;
}
</style>
